import { FileTextOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Upload
} from 'antd';
import axios from 'axios';
import React, { useState } from 'react';
import { API_BASE_URL } from '../../config';
import { auth } from '../../firebase';

const AddCandidateModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [resumeData, setResumeData] = useState(null); // Store extracted resume data (file_path, embedding, fullText)

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        message.error('User not authenticated. Please log in again.');
        return;
      }

      const idToken = await user.getIdToken();

      // Prepare data matching backend expectations
      const candidateData = {
        contactName: values.contactName,
        email: values.email || '',
        job_title: values.job_title || '',
        Location: values.location || '', // Backend expects 'Location' (capital L)
        company: values.company || '',
        linkedin_url: values.linkedin_url || '',
        phoneNumbers: values.phoneNumbers || '',
        Skills: values.skills || '',
        work_1_title: values.work_1_title || '',
        work_1_company: values.work_1_company || '',
        work_1_years: values.work_1_years || '',
        work_2_title: values.work_2_title || '',
        work_2_company: values.work_2_company || '',
        work_2_years: values.work_2_years || ''
      };

      // If resume was uploaded, include resume processing data
      if (resumeData) {
        candidateData.resume_file_path = resumeData.resume_file_path;
        candidateData.embedding = resumeData.embedding;
        candidateData.fullText = resumeData.fullText;
      }

      const response = await axios.post(`${API_BASE_URL}/api/candidates/create`, candidateData, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success(`Candidate "${values.contactName}" added successfully!${resumeData ? ' (Resume processed and embeddings generated)' : ''}`);
        form.resetFields();
        setResumeData(null); // Clear resume data after successful creation
        onSuccess && onSuccess(response.data.candidate);
        onCancel();
      }
    } catch (error) {
      console.error('Error creating candidate:', error);
      if (error.response?.status === 409) {
        message.error(error.response?.data?.error || 'A candidate with this information already exists');
      } else {
        message.error(error.response?.data?.error || 'Failed to create candidate. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setResumeData(null);
    onCancel();
  };

  // Handle resume file upload and extraction
  const handleResumeUpload = async (file) => {
    try {
      setUploadingResume(true);
      const user = auth.currentUser;
      if (!user) {
        message.error('User not authenticated. Please log in again.');
        return false; // Prevent default upload behavior
      }

      const idToken = await user.getIdToken();

      // Create form data for file upload
      const formData = new FormData();
      formData.append('resume', file);

      // Call extraction endpoint
      // Note: Don't set Content-Type manually for FormData - axios will set it with the correct boundary
      // Extended timeout to allow Gemini retries + OpenAI fallback on busy periods.
      const response = await axios.post(`${API_BASE_URL}/api/candidates/extract-from-resume`, formData, {
        headers: {
          'Authorization': `Bearer ${idToken}`
          // Content-Type will be set automatically by axios for FormData with correct boundary
        },
        timeout: 300000 // 5 minutes timeout for AI extraction and fallback path
      });

      if (response.data.success) {
        // Store resume data (file_path, embedding, fullText) for later use in submission
        setResumeData({
          resume_file_path: response.data.resume_file_path,
          embedding: response.data.embedding,
          fullText: response.data.fullText
        });

        // Pre-populate form with extracted data
        const extracted = response.data.extracted_data;
        form.setFieldsValue({
          contactName: extracted.contactName,
          email: extracted.email,
          job_title: extracted.job_title,
          location: extracted.location,
          company: extracted.company,
          linkedin_url: extracted.linkedin_url,
          phoneNumbers: extracted.phoneNumbers,
          skills: extracted.skills,
          work_1_title: extracted.work_1_title,
          work_1_company: extracted.work_1_company,
          work_1_years: extracted.work_1_years,
          work_2_title: extracted.work_2_title,
          work_2_company: extracted.work_2_company,
          work_2_years: extracted.work_2_years
        });

        message.success(response.data.message || 'Resume processed successfully! Please review and edit the information below.');
      } else {
        message.error(response.data.error || 'Failed to process resume');
      }

      return false; // Prevent default upload behavior (we handle it manually)
    } catch (error) {
      console.error('Error uploading resume:', error);
      let errorMessage = 'Failed to process resume. Please try again.';

      if (error.response) {
        // Server responded with error
        if (error.response.status === 503) {
          errorMessage = 'Service temporarily unavailable. The resume processing service may be busy or experiencing issues. Please try again in a moment.';
        } else {
          errorMessage = error.response.data?.error || error.response.data?.details || `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        // Request made but no response (likely timeout)
        errorMessage = 'Request timed out while extracting resume. The parser may be retrying provider limits. Please retry once, or wait a moment and try again.';
      } else {
        // Error setting up request
        errorMessage = error.message || 'Failed to upload resume';
      }

      message.error(errorMessage);
      return false; // Prevent default upload behavior
    } finally {
      setUploadingResume(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PlusOutlined />
          <span>Add New Candidate</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: '24px' }}
      >
        {/* Resume Upload Section */}
        <div style={{ marginBottom: '24px', padding: '16px', background: '#f0f2f5', borderRadius: '8px', border: '1px dashed #d9d9d9' }}>
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Upload Resume (Optional)</span>
            {resumeData && (
              <span style={{ fontSize: '12px', color: '#52c41a', marginLeft: '8px' }}>
                ✓ Resume processed • Embeddings ready
              </span>
            )}
          </div>
          <p style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '12px', marginTop: 0 }}>
            Upload a resume to automatically extract candidate information and generate embeddings for better search matching.
            Supported formats: PDF, DOC, DOCX (Max 5MB)
          </p>
          <Upload
            beforeUpload={handleResumeUpload}
            accept=".pdf,.doc,.docx"
            showUploadList={false}
            maxCount={1}
          >
            <Button
              icon={<UploadOutlined />}
              loading={uploadingResume}
              disabled={uploadingResume}
            >
              {uploadingResume ? 'Processing Resume...' : resumeData ? 'Replace Resume' : 'Upload Resume File'}
            </Button>
          </Upload>
        </div>

        {/* Basic Information */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>Basic Information</h3>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Full Name"
                name="contactName"
                rules={[{ required: true, message: 'Please enter the candidate\'s name' }]}
              >
                <Input placeholder="e.g., John Doe" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
              >
                <Input placeholder="e.g., john.doe@example.com" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Job Title"
                name="job_title"
              >
                <Input placeholder="e.g., Senior Trader" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Company"
                name="company"
              >
                <Input placeholder="e.g., Vitol" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Location"
                name="location"
              >
                <Input placeholder="e.g., Singapore" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="LinkedIn URL"
                name="linkedin_url"
              >
                <Input placeholder="e.g., https://linkedin.com/in/johndoe" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Phone Numbers"
                name="phoneNumbers"
                tooltip="Separate multiple numbers with commas"
              >
                <Input placeholder="e.g., +65 1234 5678, +65 9876 5432" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Skills"
                name="skills"
                tooltip="Separate multiple skills with commas"
              >
                <Input placeholder="e.g., Oil Trading, Risk Management, Python" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Work Experience */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>Work Experience</h3>

          <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 500 }}>Experience 1</h4>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Title"
                  name="work_1_title"
                >
                  <Input placeholder="e.g., Senior Trader" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Company"
                  name="work_1_company"
                >
                  <Input placeholder="e.g., Trafigura" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Years"
                  name="work_1_years"
                >
                  <Input placeholder="e.g., 5 years" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 500 }}>Experience 2</h4>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Title"
                  name="work_2_title"
                >
                  <Input placeholder="e.g., Trader" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Company"
                  name="work_2_company"
                >
                  <Input placeholder="e.g., Glencore" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Years"
                  name="work_2_years"
                >
                  <Input placeholder="e.g., 3 years" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>

        {/* Form Actions */}
        <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Add Candidate
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddCandidateModal;
